import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getUsers,
  grantSubscriptionService,
  updateUserStatus as updateUserStatusService,
} from "../../services/userService";

interface User {
  id: number;
  name: string | null;
  email: string;
  age: number | null;
  weight: number | null;
  height: number | null;
  gender: string | null;
  typeOfSubscription: string | null;
  isBlocked: boolean;
  role: string | null;
  phoneNumber: string | null;
  surname: string | null;
  createdAt: string;
  questionnaireAnswers: any[];
}

interface UserState {
  users: User[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalUsers: number;
}

const initialState: UserState = {
  users: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalUsers: 0,
};

export const fetchUsers = createAsyncThunk(
  "users/fetchUsers",
  async (
    {
      page = 1,
      limit = 10,
      name = [],
      email = [],
      role = [],
    }: {
      page?: number;
      limit?: number;
      name?: string[];
      email?: string[];
      role?: string[];
    },
    { rejectWithValue }
  ) => {
    console.log("deneme slice");

    try {
      const response = await getUsers(page, limit, name, email, role);
      return {
        ...response,
        page,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to fetch users");
    }
  }
);

export const updateUserStatus = createAsyncThunk(
  "users/updateUserStatus",
  async (
    { userId, isBlocked }: { userId: number; isBlocked: boolean },
    { rejectWithValue }
  ) => {
    try {
      if (typeof userId !== "number" || isNaN(userId)) {
        return rejectWithValue("Invalid user ID");
      }

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const updatedUser = await updateUserStatusService(userId, isBlocked);

      return { userId, isBlocked };
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update user status";

      return rejectWithValue(errorMessage);
    }
  }
);

export const grantPremiumSubscription = createAsyncThunk(
  "users/grantPremiumSubscription",
  async (
    {
      userId,
      isPremium,
      plan,
    }: {
      userId: number;
      isPremium: boolean;
      plan: "monthly" | "yearly" | "inactive";
    },
    { rejectWithValue }
  ) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const updatedUser = await grantSubscriptionService(
        userId,
        isPremium,
        plan
      );
      return { userId, isPremium, plan };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to update subscription"
      );
    }
  }
);

const userSlice = createSlice({
  name: "users",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.data;
        state.currentPage =
          action.payload.currentPage ||
          action.payload.page ||
          state.currentPage;
        state.totalPages = action.payload.totalPages || 1;
        state.totalUsers = action.payload.total || 0;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUserStatus.pending, (state, action) => {
        const { userId, isBlocked } = action.meta.arg;
        state.users = state.users.map((user) =>
          user.id === userId
            ? {
                ...user,
                isBlocked,
                isVerified: !isBlocked,
              }
            : user
        );
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        const { userId, isBlocked } = action.payload;
        state.users = state.users.map((user) =>
          user.id === userId
            ? {
                ...user,
                isBlocked,
                isVerified: !isBlocked,
              }
            : user
        );
      })
      .addCase(updateUserStatus.rejected, (state, action) => {
        const { userId } = action.meta.arg;
        state.users = state.users.map((user) =>
          user.id === userId ? { ...user, isBlocked: !user.isBlocked } : user
        );
        state.error = action.payload as string;
      })
      .addCase(grantPremiumSubscription.fulfilled, (state, action) => {
        const { userId, isPremium, plan } = action.payload;
        state.users = state.users.map((user) =>
          user.id === userId
            ? { ...user, typeOfSubscription: isPremium ? plan : null }
            : user
        );
      })
      .addCase(grantPremiumSubscription.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export default userSlice.reducer;
